import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../client';
import { useDraftMutation } from '../helpers/draftMutation';
import { applyDraftCache } from '../helpers/draftCacheHelpers';
import { reconcileEnvelope } from '../../draftGraph';
import {
  type DraftNodeEnvelope,
  type SetDraftStepManualCapacityRequest
} from '@bliks-io/contracts';
import { basePath } from './shared';

const manualCapacityPath = (processId: string, stepId: string) =>
  `${basePath(processId)}/steps/${stepId}/manual-capacity`;

/**
 * Enter or overwrite one draft step's manually entered capacity value. The
 * response node carries the value; every OTHER draft mutation answers with a
 * node loaded without it, which is why the envelope merge keeps the cached one
 * when the key is absent.
 */
export function useSetDraftStepManualCapacity(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({
      stepId,
      value
    }: {
      stepId: string;
      value: SetDraftStepManualCapacityRequest;
    }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.put<DraftNodeEnvelope>(
        manualCapacityPath(processId, stepId),
        value
      );
      return data;
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}

/** Clear the value. The response node carries an explicit `null`. */
export function useRemoveDraftStepManualCapacity(processId: string) {
  const queryClient = useQueryClient();
  return useDraftMutation(processId, {
    mutationFn: async ({ stepId }: { stepId: string }): Promise<DraftNodeEnvelope> => {
      const { data } = await apiClient.delete<DraftNodeEnvelope>(
        manualCapacityPath(processId, stepId)
      );
      return data;
    },
    onSuccess: (envelope) => {
      applyDraftCache(queryClient, processId, (old) => reconcileEnvelope(old, envelope));
    }
  });
}
